import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowRightIcon,
  UserIcon,
  EnvelopeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

// ✅ API & Context imports — DO NOT CHANGE (registration depends on these)
import { sendOtp } from '../../api/authApi';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

// ✅ Shared Tailwind input classes — no dangerouslySetInnerHTML (fixes mobile glitch)
const INPUT_CLASS = "w-full pl-12 pr-4 py-4 bg-[#0a0a0a] border border-white/5 rounded-2xl text-white outline-none focus:border-indigo-500/50 focus:bg-white/[0.08] transition-all text-sm italic placeholder-gray-600";
const INPUT_CLASS_ICON_RIGHT = "w-full pl-12 pr-10 py-4 bg-[#0a0a0a] border border-white/5 rounded-2xl text-white outline-none focus:border-indigo-500/50 focus:bg-white/[0.08] transition-all text-sm italic placeholder-gray-600";
const INPUT_CLASS_MOBILE = "w-full pl-11 pr-4 py-4 bg-[#0a0a0a] border border-white/5 rounded-2xl text-white outline-none focus:border-indigo-500/50 focus:bg-white/[0.08] transition-all text-sm italic placeholder-gray-600";

const Register = () => {
  // ✅ Auth & navigation hooks — unchanged
  const { signUp } = useAuth();
  const showToast = useToast();
  const navigate = useNavigate();
  const isSubmittingRef = useRef(false); // ✅ Double submit guard

  // ✅ Form state — unchanged
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    mobileNumber: '',
    password: '',
    confirmPassword: ''
  });

  // ✅ UI state — unchanged
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // ✅ Password strength state — unchanged
  const [strength, setStrength] = useState({
    length: false,
    number: false,
    special: false,
    upperLower: false
  });

  // ✅ Password strength checker — unchanged
  useEffect(() => {
    const pass = formData.password;
    setStrength({
      length: pass.length >= 8,
      number: /[0-9]/.test(pass),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(pass),
      upperLower: /[a-z]/.test(pass) && /[A-Z]/.test(pass)
    });
  }, [formData.password]);

  const isPasswordStrong = Object.values(strength).every(Boolean);

  // ✅ Input handler — unchanged
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobileNumber") {
      const onlyNums = value.replace(/[^0-9]/g, ''); 
      if (onlyNums.length <= 10) {
        setFormData(prev => ({ ...prev, [name]: onlyNums }));
      }
      return; 
    }
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  // ✅ Validation — unchanged
  const validate = () => {
    let newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = "Required";
    if (!formData.email.includes('@')) newErrors.email = "Invalid email";
    if (formData.mobileNumber.length !== 10) newErrors.mobileNumber = "Invalid mobile";

    if (!otpSent) {
      if (!isPasswordStrong) {
        showToast('Password requirements not met', 'error');
        newErrors.password = "Weak";
      }
      if (formData.password !== formData.confirmPassword) {
        showToast('Passwords do not match!', 'error');
        newErrors.confirmPassword = "Mismatch";
      }
      if (!termsAccepted) {
        showToast('Please accept Terms', 'error');
        newErrors.terms = "Required";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Submit handler — unchanged (sendOtp + signUp API calls intact)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmittingRef.current) return;
    if (!validate()) return;

    isSubmittingRef.current = true;
    setIsLoading(true);

    try {
      if (!otpSent) {
        // ✅ Stage 1: Send OTP
        await sendOtp(formData.email.trim(), formData.fullName.trim());
        showToast('Verification code sent!', 'success');
        setOtpSent(true);
      } else {
        // ✅ Stage 2: Complete registration
        await signUp({
          full_name: formData.fullName.trim(),
          email: formData.email.trim(),
          mobile_number: formData.mobileNumber.trim(),
          password: formData.password,
          otp_code: otpCode.trim(),
          role: 'TEAM_LEADER'
        });
        showToast('Registration Successful!', 'success');
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Error", 'error');
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] px-4 py-8 selection:bg-indigo-500/30">
      
      {/* ✅ Terms Modal — no framer-motion, simple show/hide */}
      {showTermsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80  px-4">
          <div className="w-full max-w-2xl bg-[#0a0a0a] border border-white/10 rounded-3xl shadow-2xl overflow-hidden">
            
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-[#111]">
              <h3 className="text-white font-black uppercase italic tracking-wider text-sm">
                Terms & Privacy Policy
              </h3>
              <button onClick={() => setShowTermsModal(false)} className="text-gray-500 hover:text-white transition">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 h-80 overflow-y-auto text-gray-400 text-xs leading-relaxed space-y-4">
              <div className="space-y-4">
                <p className="text-white font-bold uppercase tracking-wide">Terms & Conditions and Privacy Policy</p>
                <p>By registering on this platform, you agree to comply with and be legally bound by the following Terms & Conditions and Privacy Policy. Please read them carefully before proceeding. Your continued use of this platform signifies your acceptance of these terms.</p>
                <p><span className="text-indigo-400 font-bold uppercase">1. About the Platform : </span>Our platform is an educational and professional development initiative designed to organize coding competitions and structured internship programs. Our mission is to help students enhance their technical skills, gain practical exposure, and build industry-ready capabilities through structured learning experiences.</p>
                <p><span className="text-indigo-400 font-bold uppercase">2. Internship Programs : </span>We offer structured internship programs of 1, 2, and 3 months duration. A nominal participation fee may be charged to ensure commitment, seriousness, and effective resource allocation. Upon successful completion of all assigned tasks, evaluations, and required meetings, eligible interns may receive a certificate of completion and a one-time stipend as appreciation for their dedication and performance.</p>
                <p>Interns are required to actively participate, complete assigned tasks within deadlines, attend scheduled meetings, and maintain professional conduct throughout the internship period. Failure to meet these requirements may result in termination of the internship without refund or certification.</p>
                <p><span className="text-indigo-400 font-bold uppercase">3. Competitions & Contests : </span>We organize coding competitions aimed at promoting innovation, problem-solving, and technical excellence. Participation may require payment of a contest fee. Winners will receive rewards which may include prize money, certificates, recognition, and potential internship opportunities.</p>
                <p>Participants are strictly prohibited from engaging in cheating, plagiarism, use of unauthorized tools, or any unfair means during competitions. If any participant is found violating competition integrity guidelines, they will be immediately disqualified without refund and may be permanently restricted from future participation.</p>
                <p><span className="text-indigo-400 font-bold uppercase">4. Fair Use & Code of Conduct : </span>All users must maintain respectful and professional behavior. Any misuse of the platform, including abusive conduct, impersonation, fraudulent activity, or disruption of services, may lead to suspension or permanent account termination without prior notice.</p>
                <p><span className="text-indigo-400 font-bold uppercase">5. Scheduling & Flexibility : </span>To support student convenience, competitions and internship sessions may be organized in flexible time slots such as 5 PM, 9 PM, or 11 PM. While we aim to accommodate preferred time selections, scheduling remains subject to operational feasibility and availability.</p>
                <p><span className="text-indigo-400 font-bold uppercase">6. Payments & Refund Policy : </span>All payments made for internships or competitions are considered confirmation of participation. Fees are generally non-refundable unless explicitly stated otherwise. In exceptional circumstances, refund decisions shall remain solely at the discretion of the platform administration.</p>
                <p><span className="text-indigo-400 font-bold uppercase">7. Certification & Rewards : </span>Certificates, stipends, prizes, and recognitions are awarded only upon successful completion and verification of eligibility criteria. The platform reserves the right to withhold certification or rewards if performance standards, authenticity requirements, or conduct policies are not satisfied.</p>
                <p><span className="text-indigo-400 font-bold uppercase">8. Privacy & Data Protection : </span>We value your privacy. Personal information such as name, email, contact number, academic details, and submitted work is collected solely for registration, communication, evaluation, certification, and internal record purposes. We do not sell, rent, or trade user data to third parties.</p>
                <p>All reasonable technical and administrative security measures are implemented to protect your data from unauthorized access, misuse, or disclosure. However, while we strive for maximum security, no digital platform can guarantee absolute protection.</p>
                <p><span className="text-indigo-400 font-bold uppercase">9. Intellectual Property : </span>Participants retain ownership of their original work. However, by participating, you grant the platform limited rights to showcase submitted projects, leaderboard results, and achievements for promotional, academic, or portfolio purposes.</p>
                <p><span className="text-indigo-400 font-bold uppercase">10. Modification of Terms : </span>We reserve the right to update or modify these Terms & Conditions at any time without prior notice. Continued use of the platform after changes implies acceptance of the revised terms.</p>
                <p><span className="text-indigo-400 font-bold uppercase">11. Limitation of Liability : </span>The platform shall not be held liable for indirect, incidental, or consequential damages arising from participation in competitions or internships. Users participate at their own discretion and responsibility.</p>
                <p><span className="text-indigo-400 font-bold uppercase">12. Agreement : </span>By selecting "Accept & Continue," you acknowledge that you have read, understood, and agreed to all the above Terms & Conditions and Privacy Policy. You confirm that all information provided during registration is accurate and truthful.</p>
                <p className="text-gray-500 italic">We are committed to providing a transparent, fair, and growth-oriented ecosystem where students can upskill, compete, and gain real-world exposure in a professional and ethical environment.</p>
              </div>
            </div>

            <div className="flex justify-end gap-4 px-6 py-4 border-t border-white/10 bg-[#111]">
              <button 
                onClick={() => setShowTermsModal(false)} 
                className="px-6 py-2 text-xs font-bold uppercase tracking-wider text-gray-400 hover:text-white transition"
              >
                Close
              </button>
              <button
                onClick={() => { setTermsAccepted(true); setShowTermsModal(false); }}
                className="px-8 py-3 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-black uppercase tracking-wider rounded-xl transition-all active:scale-95"
              >
                Accept & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Main Registration Card — no framer-motion */}
    <div className="max-w-lg w-full bg-[#0a0a0a] border border-white/5 rounded-3xl shadow-xl overflow-hidden relative">
        <div className="px-8 py-10 md:px-10">
          <div className="mb-8">
            <h2 className="text-3xl font-black text-white italic uppercase tracking-tighter leading-none">
              {otpSent ? "Verify" : "Register"}
            </h2>
            <p className="text-gray-500 text-[10px] font-bold uppercase tracking-[0.3em] italic mt-2">
              {otpSent ? "Email Transmission Check" : "Create Your Account"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!otpSent ? (
              <>
                {/* Row 1: Full Name + Mobile */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputWrapper label="Full Name" icon={<UserIcon className="h-5 w-5" />}>
                    <input 
                      name="fullName" 
                      type="text" 
                      required 
                      value={formData.fullName} 
                      placeholder="Name" 
                      onChange={handleInputChange} 
                      className={INPUT_CLASS}
                    />
                  </InputWrapper>
                  <InputWrapper label="Mobile" icon={<span className="text-[10px] font-bold">+91</span>}>
                    <input 
                      name="mobileNumber" 
                      type="tel" 
                      required 
                      value={formData.mobileNumber} 
                      placeholder="Number" 
                      onChange={handleInputChange} 
                      className={INPUT_CLASS_MOBILE}
                    />
                  </InputWrapper>
                </div>

                {/* Email */}
                <InputWrapper label="Email Address" icon={<EnvelopeIcon className="h-5 w-5" />}>
                  <input 
                    name="email" 
                    type="email" 
                    required 
                    value={formData.email} 
                    placeholder="your@email.com" 
                    onChange={handleInputChange} 
                    className={INPUT_CLASS}
                  />
                </InputWrapper>

                {/* Row 2: Password + Confirm */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputWrapper label="CREATE PASSWORD" icon={<LockClosedIcon className="h-5 w-5" />}>
                    <input 
                      name="password" 
                      type={showPassword ? "text" : "password"} 
                      required 
                      value={formData.password} 
                      placeholder="Password" 
                      onChange={handleInputChange} 
                      className={INPUT_CLASS_ICON_RIGHT}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-4 text-gray-600 hover:text-indigo-400">
                      {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                    </button>
                  </InputWrapper>
                  <InputWrapper label="Confirm" icon={<LockClosedIcon className="h-5 w-5" />}>
                    <input 
                      name="confirmPassword" 
                      type={showConfirmPassword ? "text" : "password"} 
                      required 
                      value={formData.confirmPassword} 
                      placeholder="Confirm" 
                      onChange={handleInputChange} 
                      className={INPUT_CLASS_ICON_RIGHT}
                    />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-4 top-4 text-gray-600 hover:text-indigo-400">
                      {showConfirmPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                    </button>
                  </InputWrapper>
                </div>

                {/* Password Strength */}
                <div className="bg-[#111] border border-white/5 p-3 rounded-2xl grid grid-cols-2 gap-y-1 gap-x-4">
                  <StrengthItem label="8+ Chars" active={strength.length} />
                  <StrengthItem label="Mixed Case" active={strength.upperLower} />
                  <StrengthItem label="Number" active={strength.number} />
                  <StrengthItem label="Special" active={strength.special} />
                </div>

                {/* Terms */}
                <div className="flex items-center space-x-3 py-1">
                  <input 
                    type="checkbox" 
                    checked={termsAccepted} 
                    onChange={(e) => setTermsAccepted(e.target.checked)} 
                    className="h-4 w-4 accent-indigo-500 bg-transparent border-white/10 rounded cursor-pointer" 
                  />
                  <span className="text-[10px] text-gray-500 italic font-medium">
                    I AGREE TO{' '}
                    <button type="button" onClick={() => setShowTermsModal(true)} className="text-white hover:text-indigo-400 underline underline-offset-2">
                      TERMS & PRIVACY
                    </button>
                  </span>
                </div>
              </>
            ) : (
              /* ✅ OTP Stage — no framer-motion */
              <div className="space-y-4">
                <p className="text-center text-xs text-gray-500 italic">
                  Code sent to: <span className="text-indigo-400 font-bold">{formData.email}</span>
                </p>
                <input 
                  type="text" 
                  maxLength="6" 
                  required 
                  value={otpCode} 
                  onChange={(e) => setOtpCode(e.target.value)} 
                  className="w-full py-5 bg-[#0a0a0a] border-2 border-indigo-500/50 rounded-[2rem] text-white text-center text-3xl font-mono tracking-[0.5em] focus:border-indigo-500 outline-none" 
                  placeholder="000000" 
                />
                <button 
                  type="button" 
                  onClick={() => setOtpSent(false)} 
                  className="w-full text-[10px] text-gray-600 font-bold uppercase italic hover:text-white transition-colors"
                >
                  ← Edit Credentials
                </button>
              </div>
            )}

            {/* ✅ Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || (!otpSent && (!isPasswordStrong || !termsAccepted))}
              className="w-full py-5 rounded-2xl font-black text-xs uppercase tracking-[0.2em] text-white bg-indigo-600 hover:bg-indigo-500 flex items-center justify-center transition-all active:scale-95 shadow-xl shadow-indigo-600/10 italic disabled:opacity-30 disabled:grayscale disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Processing..." : otpSent ? "Finalize Setup" : "Verify Email"}
              {!isSubmitting && <ArrowRightIcon className="h-4 w-4 ml-2" />}
            </button>
          </form>
        </div>

        {/* Bottom Login Link */}
        <div className="py-6 bg-white/[0.02] border-t border-white/5 text-center px-8">
          <p className="text-gray-500 text-[10px] font-bold italic uppercase tracking-widest">
            Known Identity?{' '}
            <Link to="/login" className="text-white font-black hover:text-indigo-400 transition-colors underline underline-offset-4 decoration-indigo-500/50 ml-1">
              Login
            </Link>
          </p>
        </div>
      </div>

      {/* ✅ NO dangerouslySetInnerHTML — NO framer-motion — pure Tailwind only */}
    </div>
  );
};

// ✅ InputWrapper — pure Tailwind, no motion
const InputWrapper = ({ label, icon, children }) => (
  <div className="space-y-1 relative">
    <label className="text-[10px] font-black uppercase tracking-widest text-gray-600 italic ml-1">
      {label}
    </label>
    <div className="relative">
      <div className="absolute left-4 top-4 text-gray-600">{icon}</div>
      {children}
    </div>
  </div>
);

// ✅ StrengthItem — unchanged
const StrengthItem = ({ label, active }) => (
  <div className={`flex items-center gap-2 transition-colors ${active ? 'text-indigo-400' : 'text-gray-700'}`}>
    {active ? <CheckCircleIcon className="h-3 w-3" /> : <XCircleIcon className="h-3 w-3" />}
    <span className="text-[9px] font-black uppercase italic tracking-wider">{label}</span>
  </div>
);

export default Register;
